// ─── localStorage anahtar sabitleri ───────────────────────────────────────────
export const SOUND_STORAGE_KEY = 'emt_sound_settings';

export const DEFAULT_SOUND_SETTINGS = {
  soundEnabled:      true,
  soundNewCases:     true,   // mobile_case, planned_case, organization
  soundAssignments:  true,   // team_assignment, presidency_case_assignment
  soundEmergencies:  true,   // unassigned_case
  soundSystemAlerts: true,   // system
};

const TYPE_TO_SETTING = {
  mobile_case:                 'soundNewCases',
  planned_case:                'soundNewCases',
  organization:                'soundNewCases',
  team_assignment:             'soundAssignments',
  presidency_case_assignment:  'soundAssignments',
  unassigned_case:             'soundEmergencies',
  system:                      'soundSystemAlerts',
};

export function loadSoundSettings() {
  try {
    const stored = localStorage.getItem(SOUND_STORAGE_KEY);
    return stored
      ? { ...DEFAULT_SOUND_SETTINGS, ...JSON.parse(stored) }
      : { ...DEFAULT_SOUND_SETTINGS };
  } catch {
    return { ...DEFAULT_SOUND_SETTINGS };
  }
}

export function saveSoundSettings(settings) {
  try {
    localStorage.setItem(SOUND_STORAGE_KEY, JSON.stringify(settings));
  } catch { /* ignore */ }
}

function isSoundAllowed(notificationType) {
  const s = loadSoundSettings();
  if (!s.soundEnabled) return false;
  const key = TYPE_TO_SETTING[notificationType];
  return key ? s[key] !== false : true;
}

// ─── AudioContext ve keepalive ────────────────────────────────────────────────
let audioCtx = null;
let keepAliveTimer = null;

/**
 * Her 45 saniyede sessiz bir buffer çalarak AudioContext'in
 * Chrome tarafından otomatik suspend edilmesini önler.
 * Chrome 60s sessizlik sonrası suspend eder — 45s periyot güvenli aralıktır.
 */
function startKeepAlive() {
  if (keepAliveTimer) return;
  keepAliveTimer = setInterval(() => {
    if (!audioCtx) return;
    try {
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
        return;
      }
      if (audioCtx.state === 'running') {
        // 0 volume — tamamen sessiz, context'i uyanık tutar
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        gain.gain.setValueAtTime(0, audioCtx.currentTime);
        osc.start(audioCtx.currentTime);
        osc.stop(audioCtx.currentTime + 0.01);
      }
    } catch { /* ignore */ }
  }, 45000);
}

/**
 * Kullanıcı etkileşimi (click / keydown / touchstart) anında çağrılır.
 * AudioContext'i oluşturur, running durumuna getirir ve keepalive başlatır.
 * Layout.jsx'teki document event listener'ı tarafından tetiklenir.
 */
export function unlockAudio() {
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;

    if (!audioCtx) {
      audioCtx = new Ctor();
    }

    if (audioCtx.state === 'suspended') {
      audioCtx.resume().then(() => {
        // Safari için: sessiz buffer ile tam unlock
        const buf = audioCtx.createBuffer(1, 1, 22050);
        const src = audioCtx.createBufferSource();
        src.buffer = buf;
        src.connect(audioCtx.destination);
        src.start(0);
        startKeepAlive();
      }).catch(() => {});
    } else if (audioCtx.state === 'running') {
      startKeepAlive();
    }
  } catch { /* ignore */ }
}

// ─── Yardımcı: tek ton ───────────────────────────────────────────────────────
function tone(ctx, base, offset, freq, dur, type = 'sine', vol = 0.30) {
  const t0 = base + offset;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + 0.02);
  gain.gain.setValueAtTime(vol, t0 + dur - 0.04);
  gain.gain.linearRampToValueAtTime(0, t0 + dur);
  osc.start(t0);
  osc.stop(t0 + dur);
}

// ─── Yardımcı: frekans kayması (siren efekti) ────────────────────────────────
function sweep(ctx, base, offset, f0, f1, dur, type = 'sawtooth', vol = 0.28) {
  const t0 = base + offset;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t0);
  osc.frequency.linearRampToValueAtTime(f1, t0 + dur);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + 0.025);
  gain.gain.setValueAtTime(vol, t0 + dur - 0.04);
  gain.gain.linearRampToValueAtTime(0, t0 + dur);
  osc.start(t0);
  osc.stop(t0 + dur);
}

// ─── Ses desenleri (ctx parametresiyle) ──────────────────────────────────────

function soundMobileCase(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 920, 0.14, 'square', 0.30);
  tone(ctx, b, 0.19, 920, 0.14, 'square', 0.30);
  tone(ctx, b, 0.38, 920, 0.14, 'square', 0.30);
  tone(ctx, b, 0.60, 1100, 0.50, 'sine',   0.27);
}

function soundPlannedCase(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 659, 0.40, 'sine', 0.26);
  tone(ctx, b, 0.45, 523, 0.50, 'sine', 0.24);
}

function soundOrganization(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 523, 0.22, 'triangle', 0.26);
  tone(ctx, b, 0.25, 659, 0.22, 'triangle', 0.26);
  tone(ctx, b, 0.50, 784, 0.50, 'triangle', 0.26);
}

function soundUnassignedCase(ctx) {
  const b = ctx.currentTime + 0.02;
  sweep(ctx, b, 0.00, 1050, 680, 0.28, 'sawtooth', 0.32);
  sweep(ctx, b, 0.30, 680, 1050, 0.28, 'sawtooth', 0.32);
  sweep(ctx, b, 0.60, 1050, 680, 0.28, 'sawtooth', 0.32);
  sweep(ctx, b, 0.90, 680, 1050, 0.28, 'sawtooth', 0.32);
  tone(ctx, b, 1.28, 1050, 0.40, 'square', 0.30);
}

function soundTeamAssignment(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 784, 0.30, 'sine', 0.28);
  tone(ctx, b, 0.35, 523, 0.55, 'sine', 0.26);
}

function soundPresidencyCaseAssignment(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 440, 0.25, 'triangle', 0.28);
  tone(ctx, b, 0.28, 554, 0.25, 'triangle', 0.28);
  tone(ctx, b, 0.56, 659, 0.55, 'triangle', 0.28);
}

function soundSystem(ctx) {
  const b = ctx.currentTime + 0.02;
  tone(ctx, b, 0.00, 392, 0.35, 'sine', 0.22);
  tone(ctx, b, 0.38, 330, 0.35, 'sine', 0.20);
  tone(ctx, b, 0.76, 262, 0.55, 'sine', 0.18);
}

// ─── Tip → fonksiyon eşlemesi ─────────────────────────────────────────────────
const NOTIFICATION_SOUNDS = {
  mobile_case:                soundMobileCase,
  planned_case:               soundPlannedCase,
  organization:               soundOrganization,
  unassigned_case:            soundUnassignedCase,
  team_assignment:            soundTeamAssignment,
  presidency_case_assignment: soundPresidencyCaseAssignment,
  system:                     soundSystem,
};

/**
 * Verilen bildirim tipine göre ses çalar.
 * Context running ise anında çalar.
 * Suspended ise resume denenir, başarılı olursa çalar.
 * Context hiç oluşturulmadıysa (kullanıcı hiç tıklamamışsa) sessiz kalır.
 */
export function playNotificationSound(notificationType) {
  try {
    if (!isSoundAllowed(notificationType)) return;
    if (!audioCtx) return;

    const fn = NOTIFICATION_SOUNDS[notificationType] ?? soundSystem;

    if (audioCtx.state === 'running') {
      fn(audioCtx);
    } else if (audioCtx.state === 'suspended') {
      // Chrome otomatik suspend ettiyse tekrar başlatmayı dene
      audioCtx.resume()
        .then(() => {
          if (audioCtx.state === 'running') fn(audioCtx);
        })
        .catch(() => {});
    }
  } catch (err) {
    console.warn('Notification sound error:', err);
  }
}
